import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Users, Calendar, Plus, Edit, Trash2, UserPlus, 
  Clock, MapPin, FileText, CheckCircle, XCircle, Loader2,
  Shield, ChevronRight, AlertCircle, Settings, Key
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommitteePortal({ user }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  // Dialog states
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddMeeting, setShowAddMeeting] = useState(false);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showAddRole, setShowAddRole] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [canManageRoles, setCanManageRoles] = useState(false);
  
  // Form states
  const [newMember, setNewMember] = useState({ user_id: "", committee_role: "", title: "" });
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    description: "",
    meeting_date: "",
    start_time: "18:00",
    end_time: "19:00",
    location: "",
    meeting_type: "regular",
    agenda: []
  });

  const isAdmin = ["convenor", "captain"].includes(user?.role);
  const hasCommitteeAccess = isAdmin || user?.committee_role;

  useEffect(() => {
    if (hasCommitteeAccess) {
      fetchDashboard();
      fetchRoles();
    }
  }, [hasCommitteeAccess]);

  useEffect(() => {
    if (activeTab === "members") {
      fetchMembers();
      fetchAllUsers();
    } else if (activeTab === "meetings") {
      fetchMeetings();
    }
  }, [activeTab]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/committee/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/committee/members`, { withCredentials: true });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/committee/meetings`, { withCredentials: true });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error("Error fetching meetings:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/committee/roles`, { withCredentials: true });
      setRoles(response.data.roles || []);
      setCanManageRoles(response.data.can_manage || false);
      
      // Fetch permissions if user is convenor
      if (user?.role === "convenor") {
        try {
          const permResponse = await axios.get(`${API}/committee/roles/permissions`, { withCredentials: true });
          setAvailablePermissions(permResponse.data.permissions || []);
        } catch (err) {
          console.error("Error fetching permissions:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?authorized_only=true`, { withCredentials: true });
      // API returns array directly, not {users: [...]}
      setAllUsers(Array.isArray(response.data) ? response.data : response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.user_id || !newMember.committee_role) {
      toast.error("Please select a user and role");
      return;
    }

    try {
      await axios.post(`${API}/committee/members`, newMember, { withCredentials: true });
      toast.success("Committee member added successfully");
      setShowAddMember(false);
      setNewMember({ user_id: "", committee_role: "", title: "" });
      fetchMembers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from committee?`)) return;

    try {
      await axios.delete(`${API}/committee/members/${userId}`, { withCredentials: true });
      toast.success("Member removed from committee");
      fetchMembers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleAddMeeting = async () => {
    if (!newMeeting.title || !newMeeting.meeting_date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/committee/meetings`, newMeeting, { withCredentials: true });
      toast.success("Meeting scheduled successfully");
      setShowAddMeeting(false);
      setNewMeeting({
        title: "",
        description: "",
        meeting_date: "",
        start_time: "18:00",
        end_time: "19:00",
        location: "",
        meeting_type: "regular",
        agenda: []
      });
      fetchMeetings();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule meeting");
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm("Delete this meeting?")) return;

    try {
      await axios.delete(`${API}/committee/meetings/${meetingId}`, { withCredentials: true });
      toast.success("Meeting deleted");
      fetchMeetings();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete meeting");
    }
  };

  const handleRSVP = async (meetingId, status) => {
    try {
      await axios.post(`${API}/committee/meetings/${meetingId}/rsvp`, { status }, { withCredentials: true });
      toast.success(`RSVP: ${status}`);
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to record RSVP");
    }
  };

  // ===== ROLE MANAGEMENT FUNCTIONS =====
  const [newRole, setNewRole] = useState({ 
    id: "", 
    name: "", 
    level: 50, 
    permissions: [], 
    description: "" 
  });

  const handleCreateRole = async () => {
    if (!newRole.id || !newRole.name) {
      toast.error("Role ID and name are required");
      return;
    }
    try {
      await axios.post(`${API}/committee/roles`, newRole, { withCredentials: true });
      toast.success(`Role "${newRole.name}" created successfully`);
      setShowAddRole(false);
      setNewRole({ id: "", name: "", level: 50, permissions: [], description: "" });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create role");
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;
    try {
      await axios.put(`${API}/committee/roles/${selectedRole.id}`, {
        name: selectedRole.name,
        level: selectedRole.level,
        permissions: selectedRole.permissions,
        description: selectedRole.description
      }, { withCredentials: true });
      toast.success(`Role "${selectedRole.name}" updated`);
      setShowEditRole(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/committee/roles/${roleId}`, { withCredentials: true });
      toast.success(`Role "${roleName}" deleted`);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  const togglePermission = (permId, forNew = true) => {
    if (forNew) {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permId)
          ? prev.permissions.filter(p => p !== permId)
          : [...prev.permissions, permId]
      }));
    } else if (selectedRole) {
      setSelectedRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permId)
          ? prev.permissions.filter(p => p !== permId)
          : [...prev.permissions, permId]
      }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      convenor: "bg-purple-500/20 text-purple-400",
      section_captain: "bg-blue-500/20 text-blue-400",
      captain: "bg-blue-500/20 text-blue-400",  // Legacy mapping
      jsm_representative: "bg-orange-500/20 text-orange-400",
      vice_captain: "bg-indigo-500/20 text-indigo-400",
      treasurer: "bg-emerald-500/20 text-emerald-400",
      secretary: "bg-yellow-500/20 text-yellow-400",
      committee_member: "bg-slate-500/20 text-slate-400"
    };
    return colors[role] || colors.committee_member;
  };

  if (!hasCommitteeAccess) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-2xl mx-auto p-8 text-center bg-slate-900/50 border border-white/5 rounded-xl">
          <Shield className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Committee Access Required</h2>
          <p className="text-slate-400">You need to be a committee member to access this portal.</p>
          <Link to="/dashboard">
            <Button className="mt-4 bg-yellow-500/100 hover:bg-yellow-400 text-black">Return to Dashboard</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading committee portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin?tab=tools">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/5">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2 font-display">
                <Shield className="h-6 w-6 text-purple-400" />
                Committee Portal
              </h1>
              {dashboard && (
                <p className="text-sm text-slate-400">
                  Logged in as: {dashboard.user_info?.committee_role_display || user?.role}
                </p>
              )}
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className={`grid ${user?.role === 'convenor' ? 'grid-cols-4' : 'grid-cols-3'} w-full max-w-lg bg-slate-800/50 border border-white/5`}>
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Dashboard</TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Members</TabsTrigger>
            <TabsTrigger value="meetings" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Meetings</TabsTrigger>
            {user?.role === 'convenor' && (
              <TabsTrigger value="roles" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">
                <Settings className="w-4 h-4 mr-1" />
                Roles
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Stats Cards */}
              <Card className="bg-slate-900/50 border border-purple-500/30 rounded-xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Committee Members</p>
                      <p className="text-3xl font-bold text-purple-400">{dashboard?.members_count || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-purple-400/50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border border-blue-500/30 rounded-xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Upcoming Meetings</p>
                      <p className="text-3xl font-bold text-blue-400">{dashboard?.upcoming_meetings?.length || 0}</p>
                    </div>
                    <Calendar className="w-10 h-10 text-blue-400/50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border border-amber-500/30 rounded-xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Pending Actions</p>
                      <p className="text-3xl font-bold text-amber-400">{dashboard?.pending_action_items?.length || 0}</p>
                    </div>
                    <AlertCircle className="w-10 h-10 text-amber-400/50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Meetings */}
            <Card className="mt-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Calendar className="w-5 h-5 text-yellow-500" />
                  Upcoming Meetings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.upcoming_meetings?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.upcoming_meetings.map((meeting) => (
                      <div key={meeting.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5">
                        <div>
                          <h4 className="font-medium text-white">{meeting.title}</h4>
                          <p className="text-sm text-slate-400">
                            {formatDate(meeting.meeting_date)} at {meeting.start_time}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleRSVP(meeting.id, "attending")} className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Attending
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-4">No upcoming meetings</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Meetings */}
            {dashboard?.recent_meetings?.length > 0 && (
              <Card className="mt-6 bg-slate-900/50 border border-white/5 rounded-xl">
                <CardHeader>
                  <CardTitle className="text-white">Recent Meetings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboard.recent_meetings.map((meeting) => (
                      <div key={meeting.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5">
                        <div>
                          <h4 className="font-medium text-white">{meeting.title}</h4>
                          <p className="text-sm text-slate-400">{formatDate(meeting.meeting_date)}</p>
                        </div>
                        <Badge variant="secondary" className="bg-slate-700 text-slate-300">{meeting.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="bg-slate-900/50 border border-white/5 rounded-xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Users className="w-5 h-5 text-yellow-500" />
                    Committee Members
                  </CardTitle>
                  <CardDescription className="text-slate-400">Manage committee members and their roles</CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={() => setShowAddMember(true)} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-slate-800/50 border border-white/5 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                          <span className="text-lg font-semibold text-slate-300">
                            {member.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-white">{member.name}</h4>
                          <p className="text-sm text-slate-400">{member.email}</p>
                          {member.committee_title && (
                            <p className="text-xs text-slate-400">{member.committee_title}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.committee_role || member.role)}`}>
                          {member.committee_role_display}
                        </span>
                        {isAdmin && !["convenor", "captain"].includes(member.role) && member.committee_role && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => handleRemoveMember(member.id, member.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meetings Tab */}
          <TabsContent value="meetings">
            <Card className="bg-slate-900/50 border border-white/5 rounded-xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                    Committee Meetings
                  </CardTitle>
                  <CardDescription className="text-slate-400">Schedule and manage committee meetings</CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={() => setShowAddMeeting(true)} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {meetings.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No meetings scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {meetings.map((meeting) => (
                      <div key={meeting.id} className="bg-slate-800/50 border border-white/5 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-lg text-white">{meeting.title}</h4>
                              <Badge variant={meeting.status === "scheduled" ? "default" : "secondary"} className="bg-emerald-500/20 text-emerald-400">
                                {meeting.status}
                              </Badge>
                              <Badge variant="outline" className="border-slate-600 text-slate-300">{meeting.meeting_type}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(meeting.meeting_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {meeting.start_time} - {meeting.end_time}
                              </span>
                              {meeting.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {meeting.location}
                                </span>
                              )}
                            </div>
                            {meeting.description && (
                              <p className="text-sm text-slate-400 mt-2">{meeting.description}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Attendance: {meeting.attendance_count || 0} confirmed
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleRSVP(meeting.id, "attending")} className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRSVP(meeting.id, "not_attending")} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                              <XCircle className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleDeleteMeeting(meeting.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles Management Tab (Convenor Only) */}
          {user?.role === 'convenor' && (
            <TabsContent value="roles">
              <Card className="bg-slate-900/50 border border-white/5 rounded-xl">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Key className="w-5 h-5 text-yellow-500" />
                      Committee Roles
                    </CardTitle>
                    <CardDescription>Manage committee roles and permissions</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddRole(true)} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Role
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {roles.length === 0 ? (
                      <p className="text-center text-slate-400 py-8">No roles configured yet</p>
                    ) : (
                      roles.map((role) => (
                        <div 
                          key={role.id} 
                          className={`p-4 rounded-lg border ${role.is_system ? 'bg-slate-800/80 border-purple-500/30' : 'bg-slate-800/50 border-white/5'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-white">{role.name}</h4>
                                  {role.is_system && (
                                    <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">System</Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs border-slate-500/50 text-slate-400">
                                    Level: {role.level}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-400 mt-1">{role.description || 'No description'}</p>
                                {role.permissions?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {role.permissions.slice(0, 5).map(perm => (
                                      <Badge key={perm} variant="secondary" className="text-xs bg-slate-700/50">
                                        {perm.replace(/_/g, ' ')}
                                      </Badge>
                                    ))}
                                    {role.permissions.length > 5 && (
                                      <Badge variant="secondary" className="text-xs bg-slate-700/50">
                                        +{role.permissions.length - 5} more
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-slate-500/50 text-slate-400 hover:text-white"
                                onClick={() => {
                                  setSelectedRole({...role});
                                  setShowEditRole(true);
                                }}
                                disabled={role.id === 'convenor'}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {!role.is_system && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  onClick={() => handleDeleteRole(role.id, role.name)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Add Member Dialog */}
        <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Committee Member</DialogTitle>
              <DialogDescription>Assign a user to the committee</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select User</label>
                <Select value={newMember.user_id} onValueChange={(v) => setNewMember({ ...newMember, user_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers
                      .filter(u => !members.find(m => m.id === u.id))
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Committee Role</label>
                <Select value={newMember.committee_role} onValueChange={(v) => setNewMember({ ...newMember, committee_role: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles
                      .filter(r => r.id !== "convenor" && r.id !== "captain")
                      .map((role) => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Optional)</label>
                <Input 
                  value={newMember.title}
                  onChange={(e) => setNewMember({ ...newMember, title: e.target.value })}
                  placeholder="e.g., Academy Coordinator"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
                <Button onClick={handleAddMember}>Add Member</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Meeting Dialog */}
        <Dialog open={showAddMeeting} onOpenChange={setShowAddMeeting}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Committee Meeting</DialogTitle>
              <DialogDescription>Create a new committee meeting</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Meeting Title *</label>
                <Input 
                  value={newMeeting.title}
                  onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                  placeholder="e.g., Monthly Committee Meeting"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Date *</label>
                  <Input 
                    type="date"
                    value={newMeeting.meeting_date}
                    onChange={(e) => setNewMeeting({ ...newMeeting, meeting_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Meeting Type</label>
                  <Select value={newMeeting.meeting_type} onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="agm">AGM</SelectItem>
                      <SelectItem value="egm">EGM</SelectItem>
                      <SelectItem value="adhoc">Ad-hoc</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Time</label>
                  <Input 
                    type="time"
                    value={newMeeting.start_time}
                    onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Time</label>
                  <Input 
                    type="time"
                    value={newMeeting.end_time}
                    onChange={(e) => setNewMeeting({ ...newMeeting, end_time: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location</label>
                <Input 
                  value={newMeeting.location}
                  onChange={(e) => setNewMeeting({ ...newMeeting, location: e.target.value })}
                  placeholder="e.g., SCC Meeting Room"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea 
                  value={newMeeting.description}
                  onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                  placeholder="Meeting agenda and notes..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddMeeting(false)}>Cancel</Button>
                <Button onClick={handleAddMeeting}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Meeting
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Role Dialog */}
        <Dialog open={showAddRole} onOpenChange={setShowAddRole}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Committee Role</DialogTitle>
              <DialogDescription>Define a new role for committee members</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Role ID *</label>
                  <Input 
                    value={newRole.id}
                    onChange={(e) => setNewRole({ ...newRole, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="e.g., social_secretary"
                  />
                  <p className="text-xs text-slate-400 mt-1">Lowercase, underscores only</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Name *</label>
                  <Input 
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    placeholder="e.g., Social Secretary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Access Level (1-99)</label>
                <div className="flex items-center gap-4">
                  <Input 
                    type="number"
                    min="1"
                    max="99"
                    value={newRole.level}
                    onChange={(e) => setNewRole({ ...newRole, level: parseInt(e.target.value) || 50 })}
                    className="w-24"
                  />
                  <span className="text-sm text-slate-400">Higher = more access (Convenor is 100)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Input 
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Brief description of this role's responsibilities"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Permissions</label>
                <div className="grid grid-cols-2 gap-2 p-3 bg-slate-800/50 rounded-lg max-h-48 overflow-y-auto">
                  {availablePermissions.map(perm => (
                    <label key={perm.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-700/30 p-1 rounded">
                      <input 
                        type="checkbox"
                        checked={newRole.permissions.includes(perm.id)}
                        onChange={() => togglePermission(perm.id, true)}
                        className="rounded border-slate-500"
                      />
                      <span className="text-slate-300">{perm.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => {
                  setShowAddRole(false);
                  setNewRole({ id: "", name: "", level: 50, permissions: [], description: "" });
                }}>Cancel</Button>
                <Button onClick={handleCreateRole} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Role
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog open={showEditRole} onOpenChange={setShowEditRole}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Role: {selectedRole?.name}</DialogTitle>
              <DialogDescription>Update role settings and permissions</DialogDescription>
            </DialogHeader>
            {selectedRole && (
              <div className="space-y-4 py-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Display Name</label>
                  <Input 
                    value={selectedRole.name}
                    onChange={(e) => setSelectedRole({ ...selectedRole, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Access Level (1-99)</label>
                  <div className="flex items-center gap-4">
                    <Input 
                      type="number"
                      min="1"
                      max="99"
                      value={selectedRole.level}
                      onChange={(e) => setSelectedRole({ ...selectedRole, level: parseInt(e.target.value) || 50 })}
                      className="w-24"
                      disabled={selectedRole.is_system && selectedRole.id === 'convenor'}
                    />
                    <span className="text-sm text-slate-400">Higher = more access</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Input 
                    value={selectedRole.description || ""}
                    onChange={(e) => setSelectedRole({ ...selectedRole, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Permissions</label>
                  <div className="grid grid-cols-2 gap-2 p-3 bg-slate-800/50 rounded-lg max-h-48 overflow-y-auto">
                    {availablePermissions.map(perm => (
                      <label key={perm.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-700/30 p-1 rounded">
                        <input 
                          type="checkbox"
                          checked={selectedRole.permissions?.includes(perm.id)}
                          onChange={() => togglePermission(perm.id, false)}
                          className="rounded border-slate-500"
                        />
                        <span className="text-slate-300">{perm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowEditRole(false);
                    setSelectedRole(null);
                  }}>Cancel</Button>
                  <Button onClick={handleUpdateRole} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
                    <Edit className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
